/**
 * AddMultiplePromotersModal.tsx - Bulk Promoter Addition Modal
 *
 * This component provides an interface for adding multiple promoters to
 * fundraisers simultaneously with bulk selection and configuration options.
 *
 * Key Features:
 * - Bulk contact selection with checkboxes
 * - Contact filtering and search capabilities
 * - Bulk goal setting for selected promoters
 * - Contact list management and organization
 * - Batch processing with progress indication
 * - Selection summary and confirmation
 *
 * Bulk Operations:
 * - Multi-select contact interface
 * - Bulk goal assignment options
 * - Batch validation and error handling
 * - Progress tracking for bulk operations
 * - Success/failure reporting for each contact
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - onPromotersAdded?: (promoters: Promoter[]) => void - Success callback
 *
 * Dependencies: Contact data, bulk operations, portal rendering
 */

import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Check, Search, X } from "lucide-react";
import { Edit } from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { Toast } from "./Toast";
import {
  contactTypes,
  contactLists,
  contactInterests,
  contactTags,
  sampleContacts,
  Contact,
} from "./ContactData";
import { GoalSettingModal } from "./shared/GoalSettingModal";
import { ContactReferenceModel } from "@mindmemobile/mm-payment-modules";
import {
  ContactReferenceCountModel,
  CreateFundraiserModel,
  PromoterProgressModel,
} from "../models";
import ToastService from "./ToastService";

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 90%;
  max-width: 700px;
  height: 600px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 32px;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    /* transform: scale(1.05); */ //creating bug on hover
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const LeftColumn = styled.div`
  width: 180px;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
  flex-shrink: 0;
  overflow-y: auto;

  /* Hide scrollbar completely unless content overflows */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 0px;
  }

  /* Only show scrollbar when content actually overflows and on hover */
  &:hover::-webkit-scrollbar {
    width: 6px;
  }

  &:hover::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &:hover {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 20px;
  height: 20px;
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const NavigationMenu = styled.div`
  padding: 24px 0;
`;

const NavItem = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 12px 24px;
  border: none;
  background: ${(props) => (props.active ? "#ffffff" : "transparent")};
  color: ${(props) => (props.active ? "#1e293b" : "#64748b")};
  font-size: 16px;
  font-weight: ${(props) => (props.active ? "600" : "500")};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: ${(props) =>
    props.active ? "3px solid #142937" : "3px solid transparent"};

  &:hover {
    background: #ffffff;
    color: #1e293b;
  }
`;

const ContentArea = styled.div`
  padding: 24px 32px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(600px - 160px); /* Adjust for header and footer */
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const HeaderLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContentItem = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: ${(props) => (props.selected ? "#ffffff" : "#ffffff")};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.selected ? "#f8fafc" : "#f8fafc")};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ItemName = styled.div`
  font-size: 16px;
  font-weight: normal;
  color: #1e293b;
`;

const ItemCount = styled.div`
  font-size: 16px;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectionIndicator = styled.div<{ visible: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #10b981;
  color: #ffffff;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
`;

const ModalFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 24px 32px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
  gap: 16px;
`;

const SelectionCaption = styled.div`
  text-align: left;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const GoalCaption = styled.div`
  text-align: left;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;

  &.no-goal {
    color: #dc2626;
  }

  &.has-goal {
    color: #64748b;
  }
`;

const GoalLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  font-size: inherit;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

interface FooterButtonProps {
  variant?: "primary";
}

const FooterButton = styled.button<FooterButtonProps>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #10b981;
    color: #ffffff;

    &:hover {
      background: #059669;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

type NavigationTab = "types" | "lists" | "interests" | "tags";

interface CategoryItem {
  name: string;
  count: number;
  type: string;
}

interface AddMultiplePromotersModalProps {
  visible: boolean;
  fromDetailPage?: boolean;
  onClose: () => void;
  onSelect: (contacts: ContactReferenceModel[]) => void;
  fundraiserId: string;
  promoters: PromoterProgressModel[];
}

const getDefaultSelectedItems = (): Record<string, string[]> => ({
  types: [],
  lists: [],
  interests: [],
  tags: [],
});

export const AddMultiplePromotersModal: React.FC<
  AddMultiplePromotersModalProps
> = ({
  visible,
  fromDetailPage,
  onClose,
  onSelect,
  fundraiserId,
  promoters,
}) => {
    const [activeTab, setActiveTab] = useState<NavigationTab>("types");
    const [selectedItems, setSelectedItems] = useState<Record<string, string[]>>(
      getDefaultSelectedItems()
    );
    const [showGoalModal, setShowGoalModal] = useState(false);

    console.log(selectedItems, "selected items");
    // Handle escape key and body scroll lock
    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      if (visible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
          document.body.style.overflow = "unset";
        };
      }
    }, [visible, onClose]);

    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const selectedItemSize = selectedItems[activeTab]?.length || 0;

    const contactReferences = fundraiserStore.contactReferences;

    const handleItemClick = (type: string, itemName: string) => {
      if (type !== activeTab) return;

      const currentTabValues = selectedItems[activeTab] || [];
      const isSelected = currentTabValues.includes(itemName);

      const updatedTabValues = isSelected
        ? currentTabValues.filter((name) => name !== itemName)
        : [...currentTabValues, itemName];

      setSelectedItems({
        ...getDefaultSelectedItems(),
        [activeTab]: updatedTabValues,
      });
    };

    const getCurrentData = (): CategoryItem[] => {
      let data: ContactReferenceCountModel[] = [];
      switch (activeTab) {
        case "types":
          data = contactReferences.contactTypes;
          break;
        case "lists":
          data = contactReferences.lists;
          break;
        case "interests":
          data = contactReferences.interests;
          break;
        case "tags":
          data = contactReferences.tags;
          break;
        default:
          data = [];
      }
      return data.map((item) => ({
        name: item.name,
        count: item.count,
        type: activeTab,
      }));
    };

    const getHeaderLabels = () => {
      switch (activeTab) {
        case "types":
          return { left: "TYPES", right: "CONTACTS" };
        case "lists":
          return { left: "LISTS", right: "CONTACTS" };
        case "interests":
          return { left: "INTERESTS", right: "CONTACTS" };
        case "tags":
          return { left: "TAGS", right: "CONTACTS" };
        default:
          return { left: "", right: "" };
      }
    };

    const getSelectedContactIds = (): string[] => {
      const selectedNames = selectedItems[activeTab] || [];
      if (selectedNames.length === 0) return [];

      let source: ContactReferenceCountModel[] = [];

      switch (activeTab) {
        case "types":
          source = contactReferences.contactTypes;
          break;
        case "lists":
          source = contactReferences.lists;
          break;
        case "interests":
          source = contactReferences.interests;
          break;
        case "tags":
          source = contactReferences.tags;
          break;
      }

      const selectedContactIds = source
        .filter((item) => selectedNames.includes(item.name))
        .flatMap((item) => item.contactIds);

      return Array.from(new Set(selectedContactIds));
    };

    const getUniqueContacts = (contactIds: string[]): ContactReferenceModel[] => {
      if (contactIds.length === 0) return [];

      const contacts = fundraiserStore.contacts.filter(
        (contact) =>
          contact.mindmeContactId && contactIds.includes(contact.mindmeContactId)
          && !promoters.some((p) => p.contactId === contact.id)
          );
      return contacts;
    };

    const handleSelect = () => {
      const contactIds = getSelectedContactIds();

      if (contactIds.length === 0) return;

      const uniqueContacts = getUniqueContacts(contactIds);

      if (uniqueContacts.length === 0) {
        ToastService.showError("All selected contacts are already promoters");
        setSelectedItems(getDefaultSelectedItems());
        onClose();
        return;
      }

      onSelect(uniqueContacts);
      setSelectedItems(getDefaultSelectedItems());
      onClose();
    };

    const handleCancel = () => {
      onClose();
      setSelectedItems(getDefaultSelectedItems());
    };

    const handleGoalClick = () => {
      setShowGoalModal(true);
    };

    /* The fundraiser being worked on. On the details page this used to read and write the BUILDER's
       data, so the goal shown here and the one given to the new promoters did not match the
       fundraiser. On the details page the change applies to the promoters added now (it is not saved
       to the fundraiser's settings). */
    const goalSource = fundraiserStore.getPromoterGoalSource(fundraiserId);

    const handleGoalSave = (amount: number) => {
      if (goalSource === fundraiserStore.builderData) {
        fundraiserStore.updateBuilderField("promoterGoal", amount);
      } else if (goalSource) {
        fundraiserStore.setFundraiserDetailPageData(
          new CreateFundraiserModel({ ...goalSource, promoterGoal: amount })
        );
      }
    };

    // Check if fundraiser has a goal
    const fundraiserGoal = goalSource?.goal || 0;
    const hasFundraiserGoal = fundraiserGoal > 0;

    const formatGoalDisplay = () => {
      if (!hasFundraiserGoal) {
        return "no goal since the fundraiser has no goal";
      }

      const goal = goalSource?.promoterGoal;
      if (!goal || goal === 0) {
        return "no goal";
      }
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(goal);
    };

    const selectedContactsCount = new Set(getSelectedContactIds()).size;
    const currentData = getCurrentData();

    if (!visible) return null;

    return createPortal(
      <Overlay visible={visible} onClick={handleOverlayClick}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Add Multiple Promoters</ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <LeftColumn>
              <NavigationMenu>
                <NavItem
                  active={activeTab === "types"}
                  onClick={() => {
                    setActiveTab("types");
                    setSelectedItems(getDefaultSelectedItems());
                  }}
                >
                  Types
                </NavItem>
                <NavItem
                  active={activeTab === "lists"}
                  onClick={() => {
                    setActiveTab("lists");
                    setSelectedItems(getDefaultSelectedItems());
                  }}
                >
                  Lists
                </NavItem>
                <NavItem
                  active={activeTab === "interests"}
                  onClick={() => {
                    setActiveTab("interests");
                    setSelectedItems(getDefaultSelectedItems());
                  }}
                >
                  Interests
                </NavItem>
                <NavItem
                  active={activeTab === "tags"}
                  onClick={() => {
                    setActiveTab("tags");
                    setSelectedItems(getDefaultSelectedItems());
                  }}
                >
                  Tags
                </NavItem>
              </NavigationMenu>
            </LeftColumn>

            <RightColumn>
              <ContentArea>
                <ContentHeader>
                  <HeaderLabel>{getHeaderLabels().left}</HeaderLabel>
                  <HeaderLabel>{getHeaderLabels().right}</HeaderLabel>
                </ContentHeader>
                <ContentList>
                  {currentData.map((item) => {
                    const isSelected =
                      item.type === activeTab &&
                      (selectedItems[activeTab]?.includes(item.name) || false);
                    return (
                      <ContentItem
                        key={item.name}
                        selected={isSelected}
                        onClick={() => handleItemClick(item.type, item.name)}
                      >
                        <ItemName>{item.name}</ItemName>
                        <ItemCount>
                          {item.count}
                          <SelectionIndicator
                            visible={selectedItems[item.type]?.includes(
                              item.name
                            )}
                          >
                            <Check size={12} />
                          </SelectionIndicator>
                        </ItemCount>
                      </ContentItem>
                    );
                  })}
                </ContentList>
              </ContentArea>
            </RightColumn>
          </ModalBody>

          <ModalFooter>
            {selectedItemSize > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#64748b",
                    fontWeight: "500",
                    lineHeight: "1.5",
                    margin: "0",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span>
                    {selectedContactsCount}{" "}
                    {selectedContactsCount === 1 ? "contact" : "contacts"}{" "}
                    selected from {selectedItemSize}{" "}
                    {selectedItemSize === 1 ? activeTab.slice(0, -1) : activeTab}.
                  </span>
                  <span style={{ marginLeft: "3px" }}>
                    {" "}
                    Promoter goal will be set to{" "}
                  </span>

                  {hasFundraiserGoal ? (
                    <GoalLink
                      onClick={handleGoalClick}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {formatGoalDisplay()}
                      <Edit size={14} />
                    </GoalLink>
                  ) : (
                    <span>no goal since the fundraiser has no goal</span>
                  )}
                </p>
              </div>
            )}

            {selectedItemSize === 0 && (
              <div style={{ marginBottom: "16px" }}>
                <SelectionCaption>
                  Select {activeTab} to add contacts as promoters
                </SelectionCaption>
              </div>
            )}

            <FooterActions>
              <FooterButton onClick={handleCancel}>Cancel</FooterButton>
              <FooterButton
                variant="primary"
                onClick={handleSelect}
                disabled={selectedItemSize === 0}
              >
                Add
              </FooterButton>
            </FooterActions>
          </ModalFooter>

          <GoalSettingModal
            isVisible={showGoalModal}
            onClose={() => setShowGoalModal(false)}
            onSave={handleGoalSave}
            initialGoal={goalSource?.promoterGoal || 0}
            fundraiserGoal={fundraiserStore.builderData?.goal}
            title="Set Promoter Goal"
          />
        </ModalContent>
      </Overlay>,
      document.body
    );
  };
